// ABOUTME: The one approval card, the same in chat, Today and Approvals: what the action is in
// plain words (title and the backend's summary: what, where, when, credits), a small still of
// the post, and one row of Approve / Change / Skip, each at least 44 px tall; a paid job's card
// (`paid_job`) also offers Always approve: that kind of job then runs without asking. Once
// decided it is a one-line record ("Approved · …", "Skipped"). Change declines with what the
// owner wants different, so the agent can try again. The call's raw arguments show for admins
// only.
import { useState } from 'react'
import { Check, CheckCheck, ChevronDown, Loader2, RotateCcw, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import { selectUser, useAuthStore } from '@/stores/useAuthStore'
import type { AgentApprovalResponse } from '@/types'
import { ApprovalStill } from './ApprovalStill'
import { callerName } from './approvalGroups'

/** A paid job's card (`action_data.paid_job`): its kind and the credits it needs. */
interface PaidJob {
  kind: string
  credits: number
}

export const CHANGE_PREFIX = 'Change requested'
export const SKIPPED = 'Skipped by owner'

export interface ApprovalCardActions {
  onApprove: () => Promise<void> | void
  /** Declines with the reason: SKIPPED, or CHANGE_PREFIX and the owner's note. */
  onDecline: (reason: string) => Promise<void> | void
  onRetry?: () => Promise<void> | void
  /** Approves, and that kind of paid job runs without asking from now on. */
  onAlwaysApprove?: () => Promise<void> | void
}

interface ApprovalCardProps extends ApprovalCardActions {
  approval: AgentApprovalResponse
  busy?: boolean
  /** Approvals page bulk selection. */
  selection?: { selected: boolean; onToggle: () => void }
  highlighted?: boolean
  cardRef?: (el: HTMLDivElement | null) => void
  /** Replaces Approve when the action is reviewed elsewhere (a storyboard). */
  primary?: { label: string; onClick: () => void }
  /** Name who asked (the Approvals page lists every agent's, person's and app's cards). */
  showCaller?: boolean
}

/** "Approved · …" / "Skipped" / "Change asked: …" / "Expired" for a decided card. */
function decision(approval: AgentApprovalResponse): string | null {
  const reason = approval.rejection_reason ?? ''
  if (approval.status === 'approved') return 'Approved'
  if (approval.status === 'expired') return 'Expired'
  if (approval.status !== 'rejected') return null
  if (reason.startsWith(CHANGE_PREFIX)) {
    const note = reason.slice(CHANGE_PREFIX.length).replace(/^:\s*/, '')
    return note ? `Change asked: ${note}` : 'Change asked'
  }
  return 'Skipped'
}

function AdminDetails({ approval }: { approval: AgentApprovalResponse }) {
  return (
    <pre
      className="bg-muted/40 text-muted-foreground mt-2 max-h-48 overflow-auto rounded-md border p-2 font-mono text-[0.6875rem]"
      data-testid="approval-admin-details"
    >
      {JSON.stringify(
        { tool: approval.action_data?.tool, args: approval.action_data?.args },
        null,
        2
      )}
    </pre>
  )
}

export function ApprovalCard({
  approval,
  busy = false,
  selection,
  highlighted = false,
  cardRef,
  primary,
  showCaller = false,
  onApprove,
  onDecline,
  onRetry,
  onAlwaysApprove,
}: ApprovalCardProps) {
  const [acting, setActing] = useState<'approve' | 'always' | 'decline' | null>(null)
  const [askingChange, setAskingChange] = useState(false)
  const [note, setNote] = useState('')
  const [showDetails, setShowDetails] = useState(false)
  const isAdmin = !!(useAuthStore(selectUser) as Record<string, unknown> | null)?.is_admin
  const decided = decision(approval)
  const failed = approval.status === 'failed'
  const disabled = busy || acting !== null
  const summary = approval.description || approval.title
  const caller = callerName(approval)
  const paidJob = approval.action_data?.paid_job as PaidJob | null | undefined

  const run = async (
    kind: 'approve' | 'always' | 'decline',
    action: () => Promise<void> | void
  ) => {
    setActing(kind)
    try {
      await action()
    } finally {
      setActing(null)
    }
  }
  const sendChange = () =>
    run('decline', () =>
      onDecline(note.trim() ? `${CHANGE_PREFIX}: ${note.trim()}` : CHANGE_PREFIX)
    )

  if (decided) {
    return (
      <div
        ref={cardRef}
        className="text-muted-foreground my-1 flex min-h-8 items-center gap-2 text-sm"
        data-testid="approval-record"
        data-status={approval.status}
      >
        {approval.status === 'approved' ? (
          <Check className="text-success size-4 shrink-0" />
        ) : (
          <X className="size-4 shrink-0" />
        )}
        <span className="min-w-0 truncate">
          <span className="text-foreground font-medium">{decided}</span> · {summary}
        </span>
      </div>
    )
  }

  return (
    <div
      ref={cardRef}
      className={cn(
        'bg-card w-full rounded-xl border px-3 py-2.5 transition-shadow',
        highlighted && 'ring-primary ring-2'
      )}
      data-testid="approval-card"
      data-status={approval.status}
    >
      <div className="flex min-w-0 items-start gap-3">
        {selection && (
          <Checkbox
            checked={selection.selected}
            onCheckedChange={selection.onToggle}
            disabled={disabled}
            aria-label={`Select ${approval.title}`}
            className="mt-0.5"
          />
        )}
        <ApprovalStill approval={approval} />
        <p className="line-clamp-2 min-w-0 flex-1 text-sm break-words" data-testid="approval-text">
          <span className="font-semibold">{approval.title}</span>
          {paidJob && (
            <span className="font-medium" data-testid="approval-credits">
              {' '}
              · about {paidJob.credits} credits
            </span>
          )}
          {approval.description && (
            <span className="text-muted-foreground"> · {approval.description}</span>
          )}
          {showCaller && caller && (
            <span className="text-muted-foreground"> · asked by {caller}</span>
          )}
          {failed && <span className="text-destructive"> · It failed; retry it.</span>}
        </p>
      </div>

      {askingChange && (
        <input
          type="text"
          className="border-input bg-background mt-2 min-h-11 w-full rounded-lg border px-3 text-sm"
          placeholder="What should change?"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void sendChange()
            if (e.key === 'Escape') setAskingChange(false)
          }}
          autoFocus
          data-testid="approval-change-input"
        />
      )}

      <div className="mt-2 flex flex-wrap gap-2" data-testid="approval-actions">
        {failed && onRetry ? (
          <Button
            className="h-11 flex-1 sm:flex-none"
            onClick={() => void run('approve', onRetry)}
            disabled={disabled}
            data-testid="approval-retry"
          >
            {acting === 'approve' ? <Loader2 className="animate-spin" /> : <RotateCcw />}
            Retry
          </Button>
        ) : primary ? (
          <Button
            className="h-11 flex-1 sm:flex-none"
            onClick={primary.onClick}
            disabled={disabled}
          >
            {primary.label}
          </Button>
        ) : (
          <Button
            className="h-11 min-w-fit flex-1 sm:flex-none"
            onClick={() => void run('approve', onApprove)}
            disabled={disabled}
            data-testid="approve-button"
          >
            {acting === 'approve' || busy ? <Loader2 className="animate-spin" /> : <Check />}
            Approve
          </Button>
        )}
        {!failed && paidJob && onAlwaysApprove && (
          <Button
            variant="secondary"
            className="h-11 min-w-fit flex-1 sm:flex-none"
            onClick={() => void run('always', onAlwaysApprove)}
            disabled={disabled}
            data-testid="always-approve-button"
          >
            {acting === 'always' ? <Loader2 className="animate-spin" /> : <CheckCheck />}
            Always approve
          </Button>
        )}
        <Button
          variant="outline"
          className="h-11"
          onClick={() => (askingChange ? void sendChange() : setAskingChange(true))}
          disabled={disabled}
          data-testid="change-button"
        >
          {askingChange ? 'Send' : 'Change'}
        </Button>
        <Button
          variant="ghost"
          className="h-11"
          onClick={() => void run('decline', () => onDecline(SKIPPED))}
          disabled={disabled}
          data-testid="skip-button"
        >
          Skip
        </Button>
        {isAdmin && (
          <Button
            variant="ghost"
            size="icon-touch"
            className="text-muted-foreground ml-auto"
            aria-label="Show the call's details (admin)"
            aria-expanded={showDetails}
            onClick={() => setShowDetails(!showDetails)}
            data-testid="approval-admin-toggle"
          >
            <ChevronDown className={cn('transition-transform', showDetails && 'rotate-180')} />
          </Button>
        )}
      </div>
      {isAdmin && showDetails && <AdminDetails approval={approval} />}
    </div>
  )
}
